import React from 'react';
import { Animated } from 'react-native';
import color from 'color';
import { times } from 'lodash';
import styled from 'styled-components/native';

type Props = {
  total: number;
  active: number;
  onNext?: () => void;
  paused: boolean;
  duration: number;
};

const Container = styled.View`
  margin-top: 5px;
  margin-bottom: 5px;
  min-height: 3px;
  flex-direction: row;
`;

const Back = styled.View`
  flex: 1;
  margin-left: 1px;
  margin-right: 1px;
  background-color: ${({ theme }) =>
    color(theme.colors.onPrimary).alpha(0.5).string()};
  height: 3px;
`;

const Front = styled(Animated.View)`
  background-color: ${({ theme }) => theme.colors.onPrimary};
  width: 100%;
  flex: 1;
`;

const Component: React.FC<Props> = ({
  total,
  active,
  onNext,
  duration,
  paused,
}) => {
  const progress = React.useRef(new Animated.Value(0));
  const [remaining, setRemaining] = React.useState(0);
  const handleStart = React.useCallback<Animated.EndCallback>(
    (result) => {
      if (result.finished) {
        onNext?.();
      }
    },
    [onNext]
  );

  React.useEffect(() => {
    progress.current.setValue(0);
    setRemaining(duration);
  }, [active, duration, progress]);

  React.useEffect(() => {
    if (total === 0 || remaining === 0) {
      return;
    }
    if (paused) {
      progress.current.stopAnimation((value) => setRemaining(duration - value));
    } else {
      Animated.timing(progress.current, {
        toValue: 1,
        duration: remaining,
        useNativeDriver: false,
      }).start(handleStart);
    }
  }, [duration, handleStart, onNext, paused, progress, remaining, total]);

  return (
    <Container>
      {times(total, (index) => (
        <Back key={`${index}${active === index}`}>
          {index < active ? (
            <Front />
          ) : index === active ? (
            <Front
              style={{
                width: progress.current.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0%', '100%'],
                }),
              }}
            />
          ) : null}
        </Back>
      ))}
    </Container>
  );
};

Component.displayName = 'ProgressBars';

export default React.memo(Component);
